import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMail, HiPhone, HiLocationMarker, HiCheckCircle, HiXCircle } from 'react-icons/hi';
import { FaTwitter, FaLinkedin, FaGithub, FaMedium } from 'react-icons/fa';
import { personalInfo } from '../../config';

const ContactTab = () => {
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      inquiry: '',
      message: '',
      agreeTerms: false
    }
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setStatus({
        type: 'success',
        message: '✨ Message sent! I\'ll get back to you within 24 hours.'
      });
      reset();
      
      setTimeout(() => setStatus({ type: '', message: '' }), 5000);
    } catch (error) {
      setStatus({
        type: 'error',
        message: '❌ Something went wrong. Please try emailing me directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: <HiMail className="text-2xl" />,
      label: 'Email',
      value: personalInfo.contact.email,
      link: `mailto:${personalInfo.contact.email}`
    },
    {
      icon: <HiPhone className="text-2xl" />,
      label: 'Phone',
      value: personalInfo.contact.phone,
      link: null
    },
    {
      icon: <HiLocationMarker className="text-2xl" />,
      label: 'Location',
      value: personalInfo.contact.location,
      link: null
    }
  ];

  const socialLinks = [
    { icon: <FaTwitter />, url: personalInfo.social.twitter, label: 'Twitter' },
    { icon: <FaLinkedin />, url: personalInfo.social.linkedin, label: 'LinkedIn' },
    { icon: <FaGithub />, url: personalInfo.social.github, label: 'GitHub' },
    { icon: <FaMedium />, url: personalInfo.social.medium, label: 'Medium' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full min-h-screen px-4 py-12 sm:px-6 lg:px-8 bg-white dark:bg-gray-900"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
            Get in Touch with {personalInfo.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-base max-w-2xl mx-auto">
            Welcome to my Contact page. Whether you have questions, inquiries, or just want to say hello, 
            I'd love to hear from you. Reach out using the details below or simply fill out the contact form, 
            and I'll get back to you soon.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {contactCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="h-full"
            >
              {card.link ? (
                <a
                  href={card.link}
                  className="group relative flex flex-col items-center justify-center p-8 h-full rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-850 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="mb-4 p-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      {card.icon}
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {card.label}
                    </div>
                    <div className="text-base font-semibold text-center text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {card.value}
                    </div>
                  </div>
                </a>
              ) : (
                <div className="group relative flex flex-col items-center justify-center p-8 h-full rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-850 border-2 border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-700/10 dark:to-gray-800/10" />
                  
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="mb-4 p-4 rounded-full bg-gradient-to-br from-gray-500 to-gray-700 dark:from-gray-600 dark:to-gray-800 text-white shadow-lg">
                      {card.icon}
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                      {card.label}
                    </div>
                    <div className="text-base font-semibold text-center text-gray-900 dark:text-white">
                      {card.value}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Social Links */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-white">
            Connect With Me
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: (contactCards.length + index) * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="group relative flex flex-col items-center justify-center p-6 rounded-xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-400/10 dark:to-purple-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative z-10 flex flex-col items-center">
                  <div className="text-3xl mb-3 text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:scale-110 transition-all duration-300">
                    {social.icon}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {social.label}
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Form Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            Send Me a Message
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Have a specific inquiry or message for me? Please use the contact form below, and I'll get back to you promptly.
          </p>

          {/* Status Message */}
          <AnimatePresence>
            {status.message && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`mb-6 p-4 border ${
                  status.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                } flex items-center space-x-3`}
              >
                {status.type === 'success' ? (
                  <HiCheckCircle className="text-green-600 dark:text-green-400 text-2xl flex-shrink-0" />
                ) : (
                  <HiXCircle className="text-red-600 dark:text-red-400 text-2xl flex-shrink-0" />
                )}
                <span className="text-gray-900 dark:text-white">{status.message}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Conversational Input Line 1 */}
            <div>
              <div className="flex flex-wrap items-start gap-3 text-lg md:text-xl leading-relaxed">
                <span className="text-gray-900 dark:text-white font-normal mt-2.5">Hey, my name is</span>
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    {...register('name', {
                      required: 'Please enter your name',
                      minLength: { value: 2, message: 'Name too short' }
                    })}
                    placeholder="Your Name"
                    className={` flex items-start w-full px-4 py-2.5 bg-white dark:bg-gray-800 rounded-md border ${
                      errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all text-base`}
                  />
                  {errors.name && (
                    <div className="mt-2 flex items-start">
                      <p className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 rounded-md px-2 py-1.5 inline-block">{errors.name.message}</p>
                    </div>
                  )}
                </div>
                <span className="text-gray-900 dark:text-white font-normal mt-2.5">and I'm looking for</span>
                <div className="flex-1 min-w-[240px]">
                  <select
                    {...register('inquiry', {
                      required: 'Please select an option'
                    })}
                    className={` flex items-startw-full px-4 py-2.5 bg-white dark:bg-gray-800 rounded-md border ${
                      errors.inquiry ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all text-base cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%3E%3cpath%20d%3D%22M7%207l3-3%203%203m0%206l-3%203-3-3%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3c%2Fsvg%3E')] bg-[length:1.5em_1.5em] bg-[right_0.5rem_center] bg-no-repeat pr-10`}
                  >
                    <option value="">Select One</option>
                    <option value="web-development">Web Development</option>
                    <option value="mobile-app">Mobile App Development</option>
                    <option value="consulting">Technical Consulting</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.inquiry && (
                    <div className="mt-2  flex items-start">
                      <p className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 rounded-md px-2 py-1.5 inline-block">{errors.inquiry.message}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Conversational Input Line 2 */}
            <div>
              <div className="flex flex-wrap items-start gap-3 text-lg md:text-xl leading-relaxed">
                <span className="text-gray-900 dark:text-white font-normal mt-2.5">Get in touch with me at</span>
                <div className="flex-1 min-w-[280px]">
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Please enter your email',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    placeholder="your.email@example.com"
                    className={` flex items-start w-full px-4 py-2.5 bg-white dark:bg-gray-800 rounded-md border ${
                      errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all text-base`}
                  />
                  {errors.email && (
                    <div className="mt-2  flex items-start">
                      <p className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 rounded-md px-2 py-1.5 inline-block">{errors.email.message}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Message Field */}
            <div>
              <label className="block text-gray-900 dark:text-white text-lg mb-3 text-center">
                Tell me more about your project:
              </label>
              <textarea
                {...register('message', {
                  required: 'Please enter a message',
                  minLength: { value: 10, message: 'Message too short' }
                })}
                rows={6}
                placeholder="Describe your project, requirements, timeline, or any questions you have..."
                className={`w-full px-4 py-3 bg-white dark:bg-gray-800 rounded-md border ${
                  errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent resize-none transition-all text-base leading-relaxed`}
              />
              {errors.message && (
                <div className="mt-2  flex items-start">
                  <p className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 rounded-md px-2 py-1.5 inline-block">{errors.message.message}</p>
                </div>
              )}
            </div>

            {/* Terms Checkbox and Submit */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  {...register('agreeTerms', {
                    required: 'You must agree to continue'
                  })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 focus:ring-2 focus:ring-gray-900 dark:focus:ring-white transition-all cursor-pointer"
                />
                <label className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                  I agree with{' '}
                  <a href="/privacy-policy" className="underline hover:text-gray-900 dark:hover:text-white transition-colors">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" className="underline hover:text-gray-900 dark:hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </label>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium rounded-md hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95"
              >
                {isSubmitting ? 'Sending...' : 'Send your Message'}
              </button>
            </div>
            {errors.agreeTerms && (
              <p className="text-red-500 text-sm">{errors.agreeTerms.message}</p>
            )}
          </form>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ContactTab;